import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Select, Alert, Spin, Segmented, Tooltip } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useLiveData } from "../../../hooks/useLiveData";
import "./liveData.scss";

interface LiveDataSessionProps {
  status: { live_data_enable: boolean; live_data_period: number | null } | null;
  loading: boolean;
  error: string | null;
  startLiveData: (durationMinutes?: number) => Promise<boolean | undefined>;
  stopLiveData: () => Promise<boolean | undefined>;
  clearError: () => void;
}

const TemperatureCell = ({ temperature, device }: { temperature?: number; device?: any }) => {
  if (temperature === null || temperature === undefined) return <span>-</span>;
  
  const getColor = () => {
    if (!device) return "#012124";
    if (temperature < device.t_cold) return "#4A90E2";
    if (temperature < device.t_lukewarm) return "#7ED321";
    if (temperature < device.t_hot) return "#F5A623";
    if (temperature < device.t_very_hot) return "#D0021B";
    return "#9013FE";
  };

  return (
    <span className="value" style={{ color: getColor() }}>
      {temperature.toFixed(1)} °C
    </span>
  );
};

const StateCell = ({ state }: { state?: string }) => {
  if (!state) return <span>-</span>;
  return <span className="value">{state}</span>;
};

const WifiStrengthCell = ({ wifiStrength }: { wifiStrength?: string }) => {
  if (!wifiStrength) return <span>-</span>;
  return <span className="value">{wifiStrength}</span>;
};

const LiveData: React.FC<{ liveDataSession: LiveDataSessionProps }> = ({ liveDataSession }) => {
  const { device_id } = useParams<{ device_id: string }>();
  const { t: tSettings } = useTranslation("settings");
  const { t: tDashboard } = useTranslation("dashboard");
  const [duration, setDuration] = useState(1); 
  const [dataSegment, setDataSegment] = useState<string>("SOLISECO");

  const {
    status,
    loading: sessionLoading,
    error: sessionError,
    startLiveData,
    stopLiveData,
    clearError: clearSessionError,
  } = liveDataSession;

  const isLiveDataActive = status?.live_data_enable || false;

  const {
    data,
    loading: dataLoading,
    error: dataError,
    clearError: clearDataError,
  } = useLiveData({
    deviceId: device_id,
    enabled: isLiveDataActive,
    frequency: 10000,
    autoDisableAfterInactivity: 120000,
  });

  const handleStart = async () => {
    await startLiveData(duration);
  };

  const handleStop = async () => {
    await stopLiveData();
  };

  const error = sessionError || dataError;
  const clearError = () => {
    clearSessionError();
    clearDataError();
  };

  const fieldConfig = useMemo(() => {
    const config: Record<string, {
      segment: 'COMPTEUR' | 'SOLISECO' | 'BOTH';
      translationKey: string;
      format?: (value: any) => React.ReactNode;
      unit?: string;
    }> = {
      // METER fields
      p_tot: { segment: 'COMPTEUR', translationKey: 'last_info_p_tot', unit: ' W' },
      p_1: { segment: 'COMPTEUR', translationKey: 'last_info_p1', unit: ' W' },
      p_2: { segment: 'COMPTEUR', translationKey: 'last_info_p2', unit: ' W' },
      p_3: { segment: 'COMPTEUR', translationKey: 'last_info_p3', unit: ' W' },
      u_1: { segment: 'COMPTEUR', translationKey: 'last_info_u1', unit: ' V' },
      u_2: { segment: 'COMPTEUR', translationKey: 'last_info_u2', unit: ' V' },
      u_3: { segment: 'COMPTEUR', translationKey: 'last_info_u3', unit: ' V' },
      i_1: { segment: 'COMPTEUR', translationKey: 'last_info_i1', unit: ' A' },
      i_2: { segment: 'COMPTEUR', translationKey: 'last_info_i2', unit: ' A' },
      i_3: { segment: 'COMPTEUR', translationKey: 'last_info_i3', unit: ' A' },
      power_monthly_peak: { segment: 'COMPTEUR', translationKey: 'last_info_peak', unit: ' W' },
      power_avg_15_m: { segment: 'COMPTEUR', translationKey: 'last_info_avg', unit: ' W' },
      power_uncompensated: { segment: 'COMPTEUR', translationKey: 'last_info_power_uncompensated', unit: ' W' },
      dropout_threshold_counter: { segment: 'COMPTEUR', translationKey: 'last_info_dropout_threshold_counter' },
      energy_import_total: { segment: 'COMPTEUR', translationKey: 'last_info_energy_import_total', unit: ' kWh' },
      energy_export_total: { segment: 'COMPTEUR', translationKey: 'last_info_energy_export_total', unit: ' kWh' },
      energy_import_total_day: { segment: 'COMPTEUR', translationKey: 'last_info_energy_import_total_day', unit: ' kWh' },
      energy_import_total_night: { segment: 'COMPTEUR', translationKey: 'last_info_energy_import_total_night', unit: ' kWh' },
      energy_export_total_day: { segment: 'COMPTEUR', translationKey: 'last_info_energy_export_total_day', unit: ' kWh' },
      energy_export_total_night: { segment: 'COMPTEUR', translationKey: 'last_info_energy_export_total_night', unit: ' kWh' },
      kwh_total_export: { segment: 'COMPTEUR', translationKey: 'last_info_kwh_total_export', unit: ' kWh' },
      kwh_total_import: { segment: 'COMPTEUR', translationKey: 'last_info_kwh_total_import', unit: ' kWh' },
      // SOLISECO fields
      power_soliseco: { segment: 'SOLISECO', translationKey: 'last_info_power', unit: ' W' },
      heating_percentage_power: { segment: 'SOLISECO', translationKey: 'last_info_power_percentage', unit: ' %' },
      voltage_soliseco: { segment: 'SOLISECO', translationKey: 'last_info_voltage_soliseco', unit: ' V' },
      current_soliseco: { segment: 'SOLISECO', translationKey: 'last_info_current_soliseco', unit: ' A' },
      water_temperature: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_current_temp',
        format: (value: number) => <TemperatureCell temperature={value} />
      },
      running_state: { segment: 'SOLISECO', translationKey: 'last_info_status' },
      circulator_state: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_circulator_state',
        format: (value: string) => <StateCell state={value} />
      },
      valve_state: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_valve_state',
        format: (value: string) => <StateCell state={value} />
      },
      valve_position: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_valve_position',
        format: (value: string) => <StateCell state={value} />
      },
      valve_feedback: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_valve_feedback',
        format: (value: string) => <StateCell state={value} />
      },
      dongle_wifi_strength: { 
        segment: 'COMPTEUR', 
        translationKey: 'last_info_dongle_wifi_strength',
        format: (value: string) => <WifiStrengthCell wifiStrength={value} />
      },
      wifi_strength: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_wifi_strength',
        format: (value: string) => <WifiStrengthCell wifiStrength={value} />
      },
      is_rtc_synced: { 
        segment: 'SOLISECO', 
        translationKey: 'live_data_rtc_synced',
        format: (value: number) => <span className="value">{value ? tSettings("yes") || "Yes" : tSettings("no") || "No"}</span>
      },
      last_sanitation_days: { segment: 'SOLISECO', translationKey: 'last_info_days_since_sanitation', unit: ' days' },
      kwh_hot_water_production: { segment: 'SOLISECO', translationKey: 'last_info_kwh_hot_water_production', unit: ' kWh' },
      kwh_hot_water_solar_production: { segment: 'SOLISECO', translationKey: 'last_info_kwh_hot_water_solar_production', unit: ' kWh' },
      kwh_hot_water_non_solar_production: { segment: 'SOLISECO', translationKey: 'last_info_kwh_hot_water_non_solar_production', unit: ' kWh' },
      water_temperature_not_filtered: { segment: 'SOLISECO', translationKey: 'last_info_water_temperature_not_filtered', unit: ' °C' },
      water_temperature_filtered: { segment: 'SOLISECO', translationKey: 'last_info_water_temperature_filtered', unit: ' °C' },
      soliseco_model: { segment: 'SOLISECO', translationKey: 'last_info_soliseco_model' },
      thermostat_state: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_thermostat_state',
        format: (value: string) => <StateCell state={value} />
      },
      soliseco_temperature: { segment: 'SOLISECO', translationKey: 'last_info_soliseco_temperature', unit: ' °C' },
      is_max_temperature_reached: { 
        segment: 'SOLISECO', 
        translationKey: 'last_info_is_max_temperature_reached',
        format: (value: number) => <span className="value">{value ? tSettings("yes") || "Yes" : tSettings("no") || "No"}</span>
      },
      errors: { segment: 'SOLISECO', translationKey: 'last_info_errors' },
      dongle_ip: { segment: 'SOLISECO', translationKey: 'last_info_dongle_ip' },
      temperature_sensor: { segment: 'SOLISECO', translationKey: 'last_info_temperature_sensor' },
      available_memory: { segment: 'SOLISECO', translationKey: 'last_info_available_memory', unit: ' bytes' },
      temperature_state: { segment: 'SOLISECO', translationKey: 'last_info_temperature_state' },
      connectivity_state: { segment: 'SOLISECO', translationKey: 'last_info_connectivity_state' },
      firmware_version: { segment: 'SOLISECO', translationKey: 'last_info_firmware_version' },
      hw_version: { segment: 'SOLISECO', translationKey: 'last_info_hw_version' },
      dropout_duration_s: { segment: 'SOLISECO', translationKey: 'last_info_dropout_duration', unit: ' s' },
    };
    return config;
  }, [tSettings]);

  const fieldsToDisplay = useMemo(() => {
    if (!data) return [];
    
    return Object.keys(fieldConfig).filter(key => {
      const config = fieldConfig[key];
      const value = data[key];
      
      return value !== undefined && 
            value !== null && 
            (config.segment === dataSegment || config.segment === 'BOTH');
    });
  }, [data, dataSegment, fieldConfig]);

  return (
    <div className="live-data-container">
      <div className="live-data-controls">
        <div className="control-row">
          <label>{tSettings("live_data_duration") || "Duration (minutes)"}:</label>
          <Select
            value={duration}
            onChange={setDuration}
            disabled={isLiveDataActive}
            className="duration-select"
            options={[
              { label: "1 min", value: 1 },
              { label: "5 min", value: 5 },
              { label: "10 min", value: 10 },
              { label: "15 min", value: 15 },
              { label: "30 min", value: 30 },
            ]}
          />
          
          {!isLiveDataActive ? (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleStart}
              loading={sessionLoading}
              disabled={sessionLoading}
            >
              {tSettings("enable_live_data") || "Enable Live Data"}
            </Button>
          ) : (
            <Button
              danger
              icon={<PauseCircleOutlined />}
              onClick={handleStop}
              loading={sessionLoading}
              disabled={sessionLoading}
            >
              {tSettings("disable_live_data") || "Disable Live Data"}
            </Button>
          )}
        </div>

        {isLiveDataActive && status?.live_data_period && (
          <div className="status-info">
            <Tooltip title={tSettings("live_data_period_info") || "Live data will be active for the specified period"}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
            </Tooltip>
            <span>
              {tSettings("live_data_active") || "Active"} ({status.live_data_period / 60} {tSettings("minutes") || "min"})
            </span>
          </div>
        )}
      </div>

      {error && (
        <Alert
          message={tSettings("error") || "Error"}
          description={error}
          type="error"
          showIcon
          closable
          onClose={clearError}
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="live-data-display">
        {dataLoading && !data && (
          <div className="loading-container">
            <Spin size="large" />
            <p>{tSettings("loading") || "Loading..."}</p>
          </div>
        )}

        {!dataLoading && (!data || Object.keys(data).length === 0) && (
          <div className="no-data">
            <p>{tSettings("no_data_available") || "No data available"}</p>
            {!isLiveDataActive && (
              <p className="hint">{tSettings("enable_live_data_hint") || "Enable live data to see real-time information"}</p>
            )}
          </div>
        )}

        {data && Object.keys(data).length > 0 && (
          <div className="data-content-wrapper">
            <div className="segmented-wrapper">
            <Segmented
              options={[
                { label: tDashboard("last_info_choice_soliseco") || "SOLISECO", value: "SOLISECO" },
                { label: tDashboard("last_info_choice_meter") || "METER", value: "COMPTEUR" },
              ]}
              value={dataSegment}
              onChange={(value) => setDataSegment(value as string)}
              style={{ width: "fit-content", marginInline: "auto", marginBottom: 16 }}
            />
            </div>

            {dataSegment === "COMPTEUR" && (
              <div className="data-items">
                {fieldsToDisplay.map((key) => {
                  const config = fieldConfig[key];
                  if (config.segment !== 'COMPTEUR' && config.segment !== 'BOTH') return null;
                  
                  const value = data[key];
                  if (value === undefined || value === null) return null;
                  
                  const label = tDashboard(config.translationKey) || key;
                  
                  return (
                    <div key={key} className="data-item">
                      <span className="label">{label}</span>
                      <span className="value">
                        {config.format 
                          ? config.format(value)
                          : typeof value === 'number' 
                            ? `${value}${config.unit || ''}`
                            : String(value)
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {dataSegment === "SOLISECO" && (
              <div className="data-items">
                {fieldsToDisplay.map((key) => {
                  const config = fieldConfig[key];
                  if (config.segment !== 'SOLISECO' && config.segment !== 'BOTH') return null;
                  
                  const value = data[key];
                  if (value === undefined || value === null) return null;
                  
                  if (typeof value === 'string' && !value.trim() && config.format) return null;
                  
                  const label = config.translationKey.startsWith('live_data_')
                    ? tSettings(config.translationKey) || key
                    : tDashboard(config.translationKey) || key;
                  
                  return (
                    <div key={key} className="data-item">
                      <span className="label">{label}</span>
                      <span className="value">
                        {config.format 
                          ? config.format(value)
                          : typeof value === 'number' 
                            ? `${value}${config.unit || ''}`
                            : String(value)
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveData;
